import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import { useStorePageGuard } from '../hooks/useStorePageGuard';
import { getProductsByStore } from '../services/productService';
import { getSectorsByStore } from '../services/sectorService';
import { getStoreReviews, getStoreRating, StoreReview, StoreRating } from '../services/reviewService';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import styles from './StoreOverviewPage.module.css';

interface Statistics {
  totalProducts: number;
  totalSectors: number;
  totalCategories: number;
  totalReviews: number;
  averageRating: number;
  productsBySector: { name: string; value: number }[];
  priceDistribution: { range: string; count: number }[];
  categoryDistribution: { name: string; value: number }[];
  ratingDistribution: { rating: number; count: number }[];
}

const COLORS = ['#FABE06', '#4A90E2', '#50C878', '#FF6B6B', '#9B59B6', '#F39C12', '#1ABC9C'];

const AnimatedCounter: React.FC<{ value: number; duration?: number; decimals?: number }> = ({ 
  value, 
  duration = 2000,
  decimals = 0 
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const currentCount = progress * value;
      setCount(currentCount);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(value);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return <span>{count.toFixed(decimals)}</span>;
};

const StatCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  decimals?: number;
}> = ({ title, value, icon, color, decimals = 0 }) => {
  return (
    <div className={styles.statCard} style={{ borderTopColor: color }}>
      <div className={styles.statIcon} style={{ color }}>
        {icon}
      </div>
      <div className={styles.statContent}>
        <h3 className={styles.statTitle}>{title}</h3>
        <p className={styles.statValue} style={{ color }}>
          <AnimatedCounter value={value} decimals={decimals} />
        </p>
      </div>
    </div>
  );
};

const StoreOverviewPage: React.FC = () => {
  const { loading, store, error } = useStorePageGuard();
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  useEffect(() => {
    if (!store) return;

    const fetchStatistics = async () => {
      setIsLoadingStats(true);
      setStatsError(null);
      try {
        const [products, sectors, reviews, rating] = await Promise.all([
          getProductsByStore(store.id),
          getSectorsByStore(store.id),
          getStoreReviews(store.id).catch(() => [] as StoreReview[]),
          getStoreRating(store.id).catch(() => null as StoreRating | null),
        ]);

        const productsBySectorMap = new Map<string, number>();
        const priceRanges = {
          '0-10': 0,
          '10-25': 0,
          '25-50': 0,
          '50-100': 0,
          '100+': 0,
        };
        const categoryMap = new Map<string, number>();

        products.forEach((product) => {
          if (product.sector?.sector_name) {
            productsBySectorMap.set(
              product.sector.sector_name,
              (productsBySectorMap.get(product.sector.sector_name) || 0) + 1
            );
          }

          const price = product.price || 0;
          if (price < 10) priceRanges['0-10']++;
          else if (price < 25) priceRanges['10-25']++;
          else if (price < 50) priceRanges['25-50']++;
          else if (price < 100) priceRanges['50-100']++;
          else priceRanges['100+']++;
        });

        if (store.categories) {
          store.categories.forEach((category) => {
            if (category.category_name) {
              categoryMap.set(category.category_name, 1);
            }
          });
        }

        const productsBySector = Array.from(productsBySectorMap.entries()).map(([name, value]) => ({
          name,
          value,
        }));

        const priceDistribution = Object.entries(priceRanges).map(([range, count]) => ({
          range: `$${range}`,
          count,
        }));

        const categoryDistribution = Array.from(categoryMap.entries()).map(([name, value]) => ({
          name,
          value,
        }));

        const ratingDistributionMap = new Map<number, number>();
        reviews.forEach((review) => {
          const rating = review.rating;
          ratingDistributionMap.set(rating, (ratingDistributionMap.get(rating) || 0) + 1);
        });
        const ratingDistribution = Array.from({ length: 5 }, (_, i) => ({
          rating: i + 1,
          count: ratingDistributionMap.get(i + 1) || 0,
        }));

        setStatistics({
          totalProducts: products.length,
          totalSectors: sectors.length,
          totalCategories: store.categories?.length || 0,
          totalReviews: reviews.length,
          averageRating: rating?.averageRating || 0,
          productsBySector,
          priceDistribution,
          categoryDistribution,
          ratingDistribution,
        });
      } catch (err) {
        setStatsError('Failed to load statistics');
        console.error('Error fetching statistics:', err);
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchStatistics();
  }, [store]);

  if (loading || isLoadingStats) {
    return (
      <div className={styles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <div className={styles.loadingContainer}>
              <p>Loading store information...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error || statsError) {
    return (
      <div className={styles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <div className={styles.loadingContainer}>
              <p>{error || statsError}</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!store || !statistics) {
    return null;
  }

  return (
    <div className={styles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <div className={styles.headerSection}>
            <h1 className={styles.storeTitle}>{store.store_name}</h1>
            <p className={styles.storeSubtitle}>Store Overview & Analytics</p>
          </div>

          <div className={styles.statsGrid}>
            <StatCard
              title="Total Products"
              value={statistics.totalProducts}
              icon="📦"
              color="#4A90E2"
            />
            <StatCard
              title="Sectors"
              value={statistics.totalSectors}
              icon="🏪"
              color="#50C878"
            />
            <StatCard
              title="Categories"
              value={statistics.totalCategories}
              icon="📂"
              color="#FABE06"
            />
            <StatCard
              title="Reviews"
              value={statistics.totalReviews}
              icon="⭐"
              color="#FF6B6B"
            />
            <StatCard
              title="Average Rating"
              value={statistics.averageRating}
              icon="🌟"
              color="#9B59B6"
              decimals={1}
            />
          </div>

          <div className={styles.chartsGrid}>
            {statistics.productsBySector.length > 0 && (
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle}>Products by Sector</h3>
                <ResponsiveContainer width="100%" height={300} minWidth={0}>
                  <BarChart data={statistics.productsBySector}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill="#FABE06" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {statistics.priceDistribution.some((p) => p.count > 0) && (
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle}>Price Distribution</h3>
                <ResponsiveContainer width="100%" height={300} minWidth={0}>
                  <BarChart data={statistics.priceDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="range" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#4A90E2" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {statistics.categoryDistribution.length > 0 && (
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle}>Categories</h3>
                <ResponsiveContainer width="100%" height={300} minWidth={0}>
                  <PieChart>
                    <Pie
                      data={statistics.categoryDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statistics.categoryDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {statistics.ratingDistribution.length > 0 && (
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle}>Rating Distribution</h3>
                <ResponsiveContainer width="100%" height={300} minWidth={0}>
                  <BarChart data={statistics.ratingDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="rating" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#9B59B6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default StoreOverviewPage;
